/*
 * Color Picker Modal Controller — used by the Calendar colors settings form.
 *
 * Wraps a single color row:
 *   - a hidden form field (`field` target) holds the current hex value
 *   - a Bootstrap modal (`modal` target) shows curated palette, Ambiki defaults,
 *     and a current/custom color picker
 *   - clicking the swatch trigger opens the modal
 *   - picking a color updates the field and closes the modal
 *
 * Controllers broadcast color changes via a 'cc:color-changed' event on
 * document so that sibling pickers can update their curated palette swatch
 * labels in real time (before save).
 */
import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'field',
    'modal',
    'hexLabel',
    'preview',
    'nativeInput',
    'textInput',
    'applyButton',
    'currentSwatch',
    'currentHex',
    'swatch',
  ];

  static values = {
    label: String,
    group: String,
  };

  connect() {
    this._onColorChanged = this.refreshSwatchLabels.bind(this);
    document.addEventListener('cc:color-changed', this._onColorChanged);
  }

  disconnect() {
    document.removeEventListener('cc:color-changed', this._onColorChanged);
  }

  open(event) {
    event.preventDefault();
    this.refreshSwatchLabels();
    this.highlightSelected();
    $(this.modalTarget).modal('show');
  }

  close() {
    $(this.modalTarget).modal('hide');
  }

  pick(event) {
    const hex = event.params.hex;
    if (!hex) return;
    this.writeHex(hex);
    this.close();
  }

  pickFromNative(event) {
    const hex = event.target.value;
    if (!hex) return;
    this.writeHex(hex);
    if (this.hasTextInputTarget) {
      this.textInputTarget.value = hex.toLowerCase();
      this.validateText();
    }
  }

  validateText() {
    if (!this.hasTextInputTarget) return;
    const trimmed = this.textInputTarget.value.trim();
    const match = trimmed.match(/^#?([0-9a-fA-F]{6})$/);
    const valid = match !== null;
    if (this.hasApplyButtonTarget) {
      this.applyButtonTarget.disabled = !valid;
    }
    // Live-preview the color as the user types
    if (valid) {
      const preview = `#${match[1].toLowerCase()}`;
      if (this.hasCurrentSwatchTarget) {
        this.currentSwatchTarget.style.backgroundColor = preview;
      }
      if (this.hasCurrentHexTarget) {
        this.currentHexTarget.textContent = preview;
      }
      if (this.hasNativeInputTarget) {
        this.nativeInputTarget.value = preview;
      }
    }
  }

  applyText() {
    if (!this.hasTextInputTarget) return;
    const trimmed = this.textInputTarget.value.trim();
    const match = trimmed.match(/^#?([0-9a-fA-F]{6})$/);
    if (!match) return;
    const normalized = `#${match[1].toLowerCase()}`;
    this.writeHex(normalized);
    this.close();
  }

  submitOnEnter(event) {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    this.applyText();
  }

  writeHex(rawHex) {
    const normalized = rawHex.toLowerCase();
    this.fieldTarget.value = normalized;
    if (this.hasHexLabelTarget) {
      this.hexLabelTarget.textContent = normalized;
    }
    if (this.hasPreviewTarget) {
      this.previewTarget.style.backgroundColor = normalized;
    }
    if (this.hasNativeInputTarget) {
      this.nativeInputTarget.value = normalized;
    }
    if (this.hasCurrentSwatchTarget) {
      this.currentSwatchTarget.style.backgroundColor = normalized;
    }
    if (this.hasCurrentHexTarget) {
      this.currentHexTarget.textContent = normalized;
    }
    if (this.hasTextInputTarget) {
      this.textInputTarget.value = normalized;
    }
    this.highlightSelected();

    document.dispatchEvent(new CustomEvent('cc:color-changed'));
  }

  highlightSelected() {
    const current = this.fieldTarget.value.toLowerCase();
    this.swatchTargets.forEach((swatch) => {
      const hex = (swatch.dataset.colorPickerPopoverHexParam || '').toLowerCase();
      swatch.classList.toggle('cc-swatch-selected', hex === current);
    });
  }

  // Build a live color->label map from pickers in the same group, then update
  // this controller's curated palette swatches with labels.
  refreshSwatchLabels() {
    const colorCounts = {};
    const colorFirstLabel = {};
    const myGroup = this.groupValue;
    const groupNames = {
      'event-types': 'event types',
      'therapists': 'therapists',
      'locations': 'locations',
      'sub-types': 'sub-types',
    };
    const groupName = groupNames[myGroup] || 'items';

    document.querySelectorAll('[data-controller~="color-picker-popover"]').forEach((el) => {
      if (el.dataset.colorPickerPopoverGroupValue !== myGroup) return;
      const label = el.dataset.colorPickerPopoverLabelValue;
      const field = el.querySelector('[data-color-picker-popover-target="field"]');
      if (label && field && field.value) {
        const hex = field.value.toLowerCase();
        colorCounts[hex] = (colorCounts[hex] || 0) + 1;
        if (!colorFirstLabel[hex]) colorFirstLabel[hex] = label;
      }
    });

    this.swatchTargets.forEach((swatch) => {
      const hex = (swatch.dataset.colorPickerPopoverHexParam || '').toLowerCase();
      const count = colorCounts[hex] || 0;
      let labelSpan = swatch.querySelector('.cc-swatch-label');

      if (count > 0) {
        const text = count === 1 ? colorFirstLabel[hex] : `${count} ${groupName}`;
        swatch.classList.add('cc-swatch-labeled');
        if (!labelSpan) {
          labelSpan = document.createElement('span');
          labelSpan.className = 'cc-swatch-label';
          swatch.appendChild(labelSpan);
        }
        labelSpan.textContent = text;
      } else {
        swatch.classList.remove('cc-swatch-labeled');
        if (labelSpan) labelSpan.remove();
      }
    });
  }
}
